/**
 * DocumentModalModals — all conditionally-rendered nested modals for DocumentModal.
 *
 * Extracted from DocumentModal.tsx to reduce its render complexity.
 * This component is purely presentational: all state and business logic
 * lives in the parent, which passes data + callbacks as props.
 */

import React from 'react';
import toast from 'react-hot-toast';
import {
  type PrintPreviewTemplate,
  type DocumentConfig,
  type DocumentFormState,
} from '../documentModalHelpers';
import {
  type Document,
  type DocumentType,
  type DocumentLineItem,
  type DocumentStatus,
} from '@/hooks/useDocumentsWorkItems';
import type { Contact } from '@/app/client-zone/modules/total-finance/hooks/useContacts';
import type { MediaFile } from '@/components/shared/media/MediaLibrary';
import type { StockAvailabilityCheck as StockAvailabilityCheckType } from '@/lib/inventory/stockReservationService';
import { notify } from '@/lib/notifications';
import ItemSearchModal, { type FinanceItem, type ItemSelectionOptions } from '../ItemSearchModal';
import { type AppliedBundleResult } from '../BundleSelectModal';
import CreateShareModal from '../SendDocumentModal';
import { StakeholderOnboardingModal } from '@/components/contacts/StakeholderOnboardingModal';
import SourceReferencesModal, { type SelectedBillableItem } from '../SourceReferencesModal';
import BundleSelectModal from '../BundleSelectModal';
import BundleDesignerModal from '../BundleDesignerModal';
import RecurringScheduleModal from '../RecurringScheduleModal';
import {
  LineItemCommentThread,
} from '../../LineItemCommentThread';
import SupportingDocumentsModal from '@/components/finance/SupportingDocumentsModal';
import MediaViewer from '@/components/shared/media/MediaViewer';
import { PrintPreviewModal } from './PrintPreviewModal';
import UnifiedDocumentPreviewModal from '@/components/reports/UnifiedDocumentPreviewModal';
import { type DocumentType as ReportDocumentType } from '@/lib/reports';
import { BackorderWorkflow } from '@/components/inventory/BackorderWorkflow';
import ActivityLogModal from '@/components/work-items/ActivityLogModal';
import WorkItemDetailModal from '@/components/WorkItemDetailModal';
import { buildStakeholderContactWorkItem } from '@/components/contacts/stakeholderWorkItemAdapter';

// Duplicated locally to avoid exporting from ThemeContext (matches PrintPreviewModal pattern)
interface ClientBranding {
  primary_color?: string;
  secondary_color?: string;
  header_color?: string;
  primaryColor?: string;
  secondaryColor?: string;
  headerColor?: string;
  logo_url?: string;
  trading_name?: string;
  favicon_url?: string;
}

interface ClientDetails {
  trading_name?: string;
  registered_name?: string;
  company_registration_number?: string;
  vat_number?: string;
  physical_address_line1?: string;
  physical_address_line2?: string;
  physical_address_city?: string;
  physical_address_province?: string;
  physical_address_postal_code?: string;
  phone?: string;
  email?: string;
  bank_name?: string;
  bank_account_number?: string;
  bank_branch_code?: string;
  bank_account_type?: string;
  logo_url?: string;
}

export interface DocumentModalModalsProps {
  // --- Visibility flags + setters ---
  showItemSearch: boolean;
  setShowItemSearch: (show: boolean) => void;
  showSourceReferencesModal: boolean;
  setShowSourceReferencesModal: (show: boolean) => void;
  showBundleSelectModal: boolean;
  setShowBundleSelectModal: (show: boolean) => void;
  showBundleDesignerModal: boolean;
  setShowBundleDesignerModal: (show: boolean) => void;
  showActivityLog: boolean;
  setShowActivityLog: (show: boolean) => void;
  showSupportingDocuments: boolean;
  setShowSupportingDocuments: (show: boolean) => void;
  showRecurringModal: boolean;
  setShowRecurringModal: (show: boolean) => void;
  showShareModal: boolean;
  setShowShareModal: (show: boolean) => void;
  showUnifiedPreview: boolean;
  setShowUnifiedPreview: (show: boolean) => void;
  showPrintPreview: boolean;
  setShowPrintPreview: (show: boolean) => void;
  showStakeholderModal: boolean;
  setShowStakeholderModal: (show: boolean) => void;
  showBackorderWorkflow: boolean;
  setShowBackorderWorkflow: (show: boolean) => void;
  showStakeholderOverview: boolean;
  setShowStakeholderOverview: (show: boolean) => void;
  mediaViewerOpen: boolean;
  setMediaViewerOpen: (show: boolean) => void;
  selectedMediaIndex: number;

  // --- Line item comment thread ---
  lineItemCommentThread: { isOpen: boolean; lineItemIndex: number | null };
  setLineItemCommentThread: React.Dispatch<React.SetStateAction<{ isOpen: boolean; lineItemIndex: number | null }>>;

  // --- Unified preview ---
  previewDocumentForUnified: Document | null;
  setPreviewDocumentForUnified: React.Dispatch<React.SetStateAction<Document | null>>;

  // --- Document data ---
  formData: DocumentFormState;
  document: Document | null | undefined;
  doc_type: DocumentType;
  isPurchaseDocument: boolean;
  items: DocumentLineItem[];
  savedDocument: Document | null;
  currentDocumentId: string | null;
  config: DocumentConfig;
  selectedContact: Contact | null;
  unifiedReportDocumentType: DocumentType | null;

  // --- Print preview ---
  printTemplate: PrintPreviewTemplate | null;
  loadingTemplate: boolean;
  printPreviewTemplateLabel: string;
  printPreviewDownloadDocumentId: string | number | undefined;
  previewVisibleColumnCount: number;
  currencySymbol: string;
  formatCurrency: (value: number | string | undefined | null) => string;
  isDarkMode: boolean;
  clientBranding: ClientBranding | null;
  clientDetails: ClientDetails | null;

  // --- Media / stock ---
  mediaFiles: MediaFile[];
  insufficientStockItems: Array<{
    work_item_id: string;
    item_name?: string;
    item_code?: string;
    availability: StockAvailabilityCheckType;
  }>;

  // --- Form state setters (for unified preview email-sent callback) ---
  setFormData: React.Dispatch<React.SetStateAction<DocumentFormState>>;

  // --- Callbacks ---
  handleItemSelected: (item: FinanceItem, options: ItemSelectionOptions) => void;
  handleBillableItemsSelected: (items: SelectedBillableItem[]) => void;
  handleBackorderConfirm: (backorderItems: Array<{
    work_item_id: string;
    backorder_quantity: number;
    expected_date?: string;
  }>) => Promise<void>;
  onBundleApply: (result: AppliedBundleResult) => void;
  onShareSuccess: () => Promise<void>;
  onStakeholderSaved: (contactId?: number) => void;
  onSaved: ((doc: Document) => void) | undefined;
  onClose: () => void;
}

export function DocumentModalModals({
  showItemSearch,
  setShowItemSearch,
  showSourceReferencesModal,
  setShowSourceReferencesModal,
  showBundleSelectModal,
  setShowBundleSelectModal,
  showBundleDesignerModal,
  setShowBundleDesignerModal,
  showActivityLog,
  setShowActivityLog,
  showSupportingDocuments,
  setShowSupportingDocuments,
  showRecurringModal,
  setShowRecurringModal,
  showShareModal,
  setShowShareModal,
  showUnifiedPreview,
  setShowUnifiedPreview,
  showPrintPreview,
  setShowPrintPreview,
  showStakeholderModal,
  setShowStakeholderModal,
  showBackorderWorkflow,
  setShowBackorderWorkflow,
  showStakeholderOverview,
  setShowStakeholderOverview,
  mediaViewerOpen,
  setMediaViewerOpen,
  selectedMediaIndex,
  lineItemCommentThread,
  setLineItemCommentThread,
  previewDocumentForUnified,
  setPreviewDocumentForUnified,
  formData,
  document,
  doc_type,
  isPurchaseDocument,
  items,
  savedDocument,
  currentDocumentId,
  config,
  selectedContact,
  unifiedReportDocumentType,
  printTemplate,
  loadingTemplate,
  printPreviewTemplateLabel,
  printPreviewDownloadDocumentId,
  previewVisibleColumnCount,
  currencySymbol,
  formatCurrency,
  isDarkMode,
  clientBranding,
  clientDetails,
  mediaFiles,
  insufficientStockItems,
  setFormData,
  handleItemSelected,
  handleBillableItemsSelected,
  handleBackorderConfirm,
  onBundleApply,
  onShareSuccess,
  onStakeholderSaved,
  onSaved,
  onClose,
}: DocumentModalModalsProps) {
  return (
    <>
      {/* Item Search Modal */}
      {showItemSearch && (
        <ItemSearchModal
          isOpen={showItemSearch}
          onClose={() => setShowItemSearch(false)}
          onSelect={handleItemSelected}
          documentType={isPurchaseDocument ? 'purchase' : 'sales'}
          contactId={formData.contact_id}
          contactName={selectedContact?.display_name || selectedContact?.company_name || selectedContact?.contact_person || formData.contact_name || document?.contact_name || undefined}
          currency={formData.currency || 'ZAR'}
          scopeItemsToCustomerContext={Boolean(formData.contact_id)}
          createScopedItemForContact={Boolean(formData.contact_id)}
        />
      )}

      {/* Source References Modal - Import from Jobs/Tickets/Tasks */}
      {showSourceReferencesModal && (
        <SourceReferencesModal
          isOpen={showSourceReferencesModal}
          onClose={() => setShowSourceReferencesModal(false)}
          onImport={handleBillableItemsSelected}
          contactId={formData.contact_id}
          contactName={selectedContact?.display_name || selectedContact?.company_name || selectedContact?.contact_person}
        />
      )}

      {/* Bundle Template Select Modal */}
      {showBundleSelectModal && (
        <BundleSelectModal
          isOpen={showBundleSelectModal}
          onClose={() => setShowBundleSelectModal(false)}
          documentType={doc_type}
          onApply={onBundleApply}
          onManageTemplates={() => {
            setShowBundleSelectModal(false);
            setShowBundleDesignerModal(true);
          }}
        />
      )}

      {/* Bundle Designer Modal - for creating/editing templates */}
      {showBundleDesignerModal && (
        <BundleDesignerModal
          isOpen={showBundleDesignerModal}
          onClose={() => setShowBundleDesignerModal(false)}
          onSaved={() => {
            setShowBundleDesignerModal(false);
            toast.success('Bundle template saved!');
          }}
        />
      )}

      {/* Line Item Comment Thread Modal */}
      {lineItemCommentThread.isOpen && lineItemCommentThread.lineItemIndex !== null && currentDocumentId && (
        <LineItemCommentThread
          documentId={currentDocumentId}
          lineItemId={String(lineItemCommentThread.lineItemIndex)}
          lineItemDescription={items[lineItemCommentThread.lineItemIndex]?.description || 'Line item'}
          isOpen={lineItemCommentThread.isOpen}
          onClose={() => setLineItemCommentThread({ isOpen: false, lineItemIndex: null })}
        />
      )}

      {/* Activity Log Modal (Finance documents) */}
      {showActivityLog && currentDocumentId && (
        <ActivityLogModal
          workItemId={currentDocumentId}
          documentType={doc_type}
          onClose={() => setShowActivityLog(false)}
        />
      )}

      {/* Supporting Documents Modal (Finance documents) */}
      {showSupportingDocuments && currentDocumentId && (
        <SupportingDocumentsModal
          isOpen={showSupportingDocuments}
          onClose={() => setShowSupportingDocuments(false)}
          workItemId={currentDocumentId}
        />
      )}

      {/* Recurring Schedule Modal */}
      {showRecurringModal && document && (
        <RecurringScheduleModal
          isOpen={showRecurringModal}
          onClose={() => setShowRecurringModal(false)}
          document={{
            ...document,
            doc_type: doc_type,
            contact_id: formData.contact_id,
            contact_name: formData.contact_name,
            contact_email: formData.contact_email,
            billing_address: formData.billing_address,
            shipping_address: formData.shipping_address,
            currency: formData.currency || 'ZAR',
            payment_terms: formData.payment_terms,
            notes: formData.notes,
            terms_and_conditions: formData.terms_and_conditions,
            reference: formData.reference,
            items: items
          }}
          onSaved={(schedule) => {
            setShowRecurringModal(false);
            notify.success(`Recurring schedule "${schedule.name}" created successfully!`);
          }}
        />
      )}

      {/* Share Modal */}
      {showShareModal && savedDocument && (
        <CreateShareModal
          isOpen={showShareModal}
          onClose={() => {
            setShowShareModal(false);
            onSaved?.(savedDocument);
            onClose();
          }}
          onSuccess={onShareSuccess}
          preSelectedDocumentId={savedDocument.id}
          preSelectedDocumentType={doc_type}
          preSelectedDocument={savedDocument}
        />
      )}

      {showUnifiedPreview && unifiedReportDocumentType && previewDocumentForUnified?.id && (
        <UnifiedDocumentPreviewModal
          documentId={previewDocumentForUnified.id}
          documentType={unifiedReportDocumentType as ReportDocumentType}
          documentNumber={previewDocumentForUnified.doc_number || previewDocumentForUnified.item_number || null}
          title={`${config.labels.title} Preview`}
          defaultEmailTo={formData.contact_email || null}
          defaultEmailName={formData.contact_name || null}
          isOpen={showUnifiedPreview}
          onClose={() => setShowUnifiedPreview(false)}
          onEmailSent={() => {
            setFormData((prev) => ({
              ...prev,
              status: prev.status === 'sent' ? prev.status : 'sent' as DocumentStatus,
            }));
            setPreviewDocumentForUnified((prev) => (prev
              ? { ...prev, status: 'sent' as DocumentStatus }
              : prev
            ));
          }}
        />
      )}

      {/* Print Preview Modal */}
      <PrintPreviewModal
        show={showPrintPreview}
        onClose={() => setShowPrintPreview(false)}
        formData={formData}
        items={items}
        config={config}
        printTemplate={printTemplate}
        loadingTemplate={loadingTemplate}
        printPreviewTemplateLabel={printPreviewTemplateLabel}
        printPreviewDownloadDocumentId={printPreviewDownloadDocumentId}
        previewVisibleColumnCount={previewVisibleColumnCount}
        currencySymbol={currencySymbol}
        formatCurrency={formatCurrency}
        isDarkMode={isDarkMode}
        isPurchaseDocument={isPurchaseDocument}
        clientBranding={clientBranding}
        clientDetails={clientDetails}
        selectedContact={selectedContact}
      />

      <StakeholderOnboardingModal
        isOpen={showStakeholderModal}
        onClose={() => setShowStakeholderModal(false)}
        onSaved={onStakeholderSaved}
        defaultContactType={isPurchaseDocument ? 'supplier' : 'customer'}
        mode="create"
        moduleKey="total-finance"
      />

      {/* Backorder Workflow Modal */}
      {showBackorderWorkflow && (
        <BackorderWorkflow
          insufficientItems={insufficientStockItems}
          onConfirm={handleBackorderConfirm}
          onCancel={() => setShowBackorderWorkflow(false)}
          isDarkMode={isDarkMode}
        />
      )}

      {/* Stakeholder Detail Modal — opens full WorkItemDetailModal with stakeholder tabs */}
      <WorkItemDetailModal
        isOpen={showStakeholderOverview}
        onClose={() => setShowStakeholderOverview(false)}
        workItem={selectedContact?.id ? buildStakeholderContactWorkItem(selectedContact as Parameters<typeof buildStakeholderContactWorkItem>[0]) : null}
        visibleTabs={['overview', 'stakeholder_people', 'stakeholder_locations', 'stakeholder_documents', 'stakeholder_hierarchy', 'stakeholder_time_tracking', 'stakeholder_assets', 'history']}
        initialTab="overview"
        moduleColorKey="total-finance"
        containerZIndex={140}
      />

      {/* Media Viewer for full-screen photo viewing from PhotoSidePanel */}
      <MediaViewer
        isOpen={mediaViewerOpen}
        onClose={() => setMediaViewerOpen(false)}
        media={mediaViewerOpen && mediaFiles[selectedMediaIndex] ? mediaFiles[selectedMediaIndex] : null}
        isDark={isDarkMode}
        permissions={{ can_edit: false, can_delete: false, can_publish_resources: false }}
      />
    </>
  );
}

export default DocumentModalModals;
